import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Facebook, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import { getMetaPages, updateMetaPageConfig, getPageCategories } from '@/services/api';
import { MetaPage, UpdatePageConfigInput } from './types/meta.types';
import { TIMEZONES, LANGUAGES, CURRENCIES } from './constants/config-options';
import PageVariablesPanel from './components/PageVariablesPanel';
import PageAppCapabilitiesPanel from './components/PageAppCapabilitiesPanel';

type PageTab = 'details' | 'variables' | 'app-capabilities';

export default function PageDetails() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [activeTab, setActiveTab] = useState<PageTab>('details');
    const [formData, setFormData] = useState<UpdatePageConfigInput>({
        timezone: 'UTC',
        currency: 'USD',
        language: 'pt_BR',
        internal_category_id: null,
    });

    const { data: pages = [], isLoading } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: getMetaPages,
    });

    const { data: categories = [] } = useQuery({
        queryKey: ['page-categories'],
        queryFn: getPageCategories,
    });

    const page: MetaPage | undefined = pages.find((p) => p.id === Number(id));

    useEffect(() => {
        if (page) {
            setFormData({
                timezone: page.timezone || 'UTC',
                currency: page.currency || 'USD',
                language: page.language || 'pt_BR',
                internal_category_id: page.internal_category_id,
            });
        }
    }, [page]);

    const saveMutation = useMutation({
        mutationFn: ({ pageId, data }: { pageId: number; data: UpdatePageConfigInput }) =>
            updateMetaPageConfig(pageId, data),
        onSuccess: () => {
            toast({ title: 'Configurações salvas!', description: 'As informações da página foram atualizadas.' });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro ao salvar as configurações.',
            });
        },
    });

    const handleSave = () => {
        if (page) {
            saveMutation.mutate({ pageId: page.id, data: formData });
        }
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f172a]" />
                        <p className="ml-3 text-gray-500">Carregando página...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!page) {
        return (
            <div className="space-y-6">
                <div className="rounded-2xl bg-white p-12 shadow-sm border border-gray-100 text-center">
                    <p className="text-gray-500 mb-4">Página não encontrada.</p>
                    <Button onClick={() => navigate('/meta/connection')} variant="outline">
                        Voltar
                    </Button>
                </div>
            </div>
        );
    }

    const createdAt = new Date(page.created_at).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    return (
        <div className="space-y-6">
            {/* Back button */}
            <button
                onClick={() => navigate('/meta/connection')}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
                <ArrowLeft className="w-4 h-4" />
                Voltar para Conexões
            </button>

            {/* Page Header Card */}
            <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100">
                            <Facebook className="w-7 h-7 text-blue-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{page.page_name}</h1>
                            <div className="flex items-center gap-3 mt-1">
                                <span className="text-xs text-gray-400 font-mono">ID: {page.page_id}</span>
                                <span className="text-gray-300">·</span>
                                <span className="text-xs text-gray-400">Facebook</span>
                                <span className="text-gray-300">·</span>
                                {page.is_active ? (
                                    <Badge variant="success" className="text-xs">Ativa</Badge>
                                ) : (
                                    <Badge variant="destructive" className="text-xs">Inativa</Badge>
                                )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Criada em {createdAt}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-1 border-b border-gray-200">
                {([
                    { id: 'details', label: 'Detalhes' },
                    { id: 'variables', label: 'Variáveis' },
                    { id: 'app-capabilities', label: 'Funções por App' },
                ] as { id: PageTab; label: string }[]).map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px ${activeTab === tab.id
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-900'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Details Tab */}
            {activeTab === 'details' && (
                <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6 space-y-6">
                    <h2 className="text-lg font-bold text-gray-900">Configurações da Página</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Timezone */}
                        <div className="space-y-2">
                            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Fuso Horário
                            </Label>
                            <SearchableSelect
                                value={formData.timezone}
                                onValueChange={(v) => setFormData({ ...formData, timezone: v })}
                                options={TIMEZONES}
                                searchPlaceholder="Buscar fuso horário..."
                            />
                        </div>

                        {/* Category */}
                        <div className="space-y-2">
                            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Categoria
                            </Label>
                            <SearchableSelect
                                value={formData.internal_category_id?.toString() || 'none'}
                                onValueChange={(v) => setFormData({
                                    ...formData,
                                    internal_category_id: v === 'none' ? null : parseInt(v),
                                })}
                                options={[
                                    { value: 'none', label: 'Nenhuma' },
                                    ...categories.map((cat) => ({ value: cat.id.toString(), label: cat.name })),
                                ]}
                                placeholder="Selecione uma categoria"
                                searchPlaceholder="Buscar categoria..."
                            />
                        </div>

                        {/* Currency */}
                        <div className="space-y-2">
                            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Moeda
                            </Label>
                            <SearchableSelect
                                value={formData.currency}
                                onValueChange={(v) => setFormData({ ...formData, currency: v })}
                                options={CURRENCIES}
                                searchPlaceholder="Buscar moeda..."
                            />
                        </div>

                        {/* Language */}
                        <div className="space-y-2">
                            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Idioma
                            </Label>
                            <SearchableSelect
                                value={formData.language}
                                onValueChange={(v) => setFormData({ ...formData, language: v })}
                                options={LANGUAGES}
                                searchPlaceholder="Buscar idioma..."
                            />
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-2">
                        <Button
                            onClick={handleSave}
                            disabled={saveMutation.isPending}
                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8"
                        >
                            <Check className="w-4 h-4 mr-2" />
                            {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                </div>
            )}

            {/* Variables Tab */}
            {activeTab === 'variables' && (
                <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-6">Variáveis da Página</h2>
                    <PageVariablesPanel page={page} />
                </div>
            )}

            {/* App Capabilities Tab */}
            {activeTab === 'app-capabilities' && (
                <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-6">
                    <PageAppCapabilitiesPanel page={page} />
                </div>
            )}
        </div>
    );
}
